"use client"

import { useContext, useEffect, useState } from "react"
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Close as CloseIcon, PointOfSale as PointOfSaleIcon } from "@mui/icons-material"
import { ApiContextDominios } from "../../../context/apiContextDominios.js"
import { useTenantNavigation } from "../../../hooks/useTenantNavigation"
import SpinnerLading from "../../misc/SpinnerComponent.js"

const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString("es-AR")
}

const formatMoney = (value) => {
  const amount = Number.parseFloat(value || 0)
  return amount.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
}

const ModalViewTablaPreVenta = ({ openModal, handleCloseModal, selectedTransaccion }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const navigate = useTenantNavigation()
  const { getPreventaDetail } = useContext(ApiContextDominios)

  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)

  useEffect(() => {
    const fetchDetail = async () => {
      if (!openModal || !selectedTransaccion?.id) return
      try {
        setLoading(true)
        const response = await getPreventaDetail(selectedTransaccion.id)
        setDetail(response)
      } catch (error) {
        console.error("Error al cargar detalle de preventa:", error)
        setDetail(null)
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [openModal, selectedTransaccion, getPreventaDetail])

  const handlePasarAVenta = () => {
    if (!selectedTransaccion?.id) return
    navigate(`ventaCrear/${selectedTransaccion.id}?fromPreventa=true`)
  }

  const transaccion = detail?.transaccion || selectedTransaccion
  const items = detail?.items || []

  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "calc(100% - 24px)" : "min(980px, 96vw)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Paper sx={{ borderRadius: 2 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: "#5b5b5f",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Detalle Preventa #{transaccion?.id || "-"}</Typography>
            <IconButton onClick={handleCloseModal} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.5 }}>
            {loading ? (
              <SpinnerLading />
            ) : (
              <>
                <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={`Cliente: ${transaccion?.descripcionEntidad || "-"} ${transaccion?.apellido || ""}`.trim()}
                  />
                  <Chip label={`Ubicación: ${transaccion?.ubicacion || "-"}`} />
                  <Chip label={`Usuario: ${transaccion?.usuario || "-"}`} />
                  <Chip label={`Fecha: ${formatDate(transaccion?.fechaHoraCreacion || transaccion?.createdAt)}`} />
                  <Chip color="primary" label={`Total: ${formatMoney(transaccion?.montoTotal)}`} />
                </Stack>

                <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID Item</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Precio</TableCell>
                        <TableCell align="right">% Desc.</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No hay items cargados en esta preventa.
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map((item) => {
                          const subtotal = Number.parseFloat(item.cantidad || 0) * Number.parseFloat(item.precio || 0)
                          return (
                            <TableRow key={`${item.idItem}-${item.id}`}>
                              <TableCell>{item.idItem}</TableCell>
                              <TableCell align="right">{item.cantidad}</TableCell>
                              <TableCell align="right">{formatMoney(item.precio)}</TableCell>
                              <TableCell align="right">{item.porcentajeDescuento || 0}</TableCell>
                              <TableCell align="right">{formatMoney(subtotal)}</TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
                  <Button onClick={handleCloseModal} variant="outlined">
                    Cerrar
                  </Button>
                  <Button
                    onClick={handlePasarAVenta}
                    variant="contained"
                    color="success"
                    startIcon={<PointOfSaleIcon />}
                  >
                    Pasar a venta
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Modal>
  )
}

export default ModalViewTablaPreVenta
